import {
    CENTRA_LOCALE_SHIPPING_COUNTRY_CODES,
    CentraLocaleShippingCountryCode
} from "@/data/centra-locale-shipping-country-code"
import {
    SHIPPING_COUNTRY_CODES,
    ShippingCountryCode,
    isShippingCountryCode
} from "@/data/shipping-country-code"
import { I18n, i18n, validateCentraLocale } from "@/i18n.config"
import shippingCountryCodeToPriceList from "@/maps/shipping-country-code-to-pricelist"
import { TokenAndSelectionMetadata, UserLocale } from "@/types/cookie-types"
import { z } from "zod"
import Logger from "../logger/Logger"

class CentraPath {
    constructor(
        private logger: Logger,
        private validateCentraLocaleShippingCountryCode: (
            code: string
        ) => z.SafeParseReturnType<
            CentraLocaleShippingCountryCode,
            CentraLocaleShippingCountryCode
        >
    ) {}

    public stripLeadingLocale = (path: string): string => {
        const localePattern = /^[a-z]{2}-[A-Za-z]{2}$/

        const pathSegments = path.split("/")

        if (
            (pathSegments.length > 1 && localePattern.test(pathSegments[1])) ||
            this.isStringEuroCurrencyCentraLocale(pathSegments[1])
        ) {
            return `/${pathSegments.slice(2).join("/")}`
        }

        return path
    }

    public stripLeadingLegalCentraLocale = (path: string): string => {
        const localePattern = /^[a-z]{2}-[A-Za-z]{2}$/

        const pathSegments = path.split("/")

        if (
            (pathSegments.length > 1 && localePattern.test(pathSegments[1])) ||
            this.isStringEuroCurrencyCentraLocale(pathSegments[1])
        ) {
            return this.isIllegalCentraLocale(i18n, pathSegments[1])
                ? `/${pathSegments.slice(1).join("/")}`
                : `/${pathSegments.slice(2).join("/")}`
        }

        return path
    }

    private normalizeSlashes = (pathname: string) => {
        return pathname.replace(/\/\/+/g, "/")
    }

    public updateCurrentPathWithCentraLocale = (
        currentPath: string,
        centraLocaleToUpdateTo: string
    ) => {
        const centraLocaleShippingCountryFromLocale =
            this.isStringEuroCurrencyCentraLocale(centraLocaleToUpdateTo)
                ? ""
                : centraLocaleToUpdateTo.split("-")[1] ?? ""

        const centraLocaleShippingCountryValidation =
            this.validateCentraLocaleShippingCountryCode(
                centraLocaleShippingCountryFromLocale
            )

        if (!centraLocaleShippingCountryValidation.success) {
            this.logger.error(
                `Invalid Centra locale shipping country code: ${centraLocaleShippingCountryFromLocale}`
            )

            return ""
        }

        let newPathName = `/${centraLocaleToUpdateTo}`
        if (currentPath !== "/") {
            newPathName += `/${this.stripLeadingLegalCentraLocale(currentPath)}`
        }

        const normalizedNewPathname = this.normalizeSlashes(newPathName)

        return normalizedNewPathname
    }

    public getCentraLocaleFromUrl = (url: string) => {
        const urlObj = new URL(url)

        const pathParts = urlObj.pathname.split("/")

        const locale = pathParts[1]

        return locale ? locale : "/"
    }

    public isValidLocale = (locale: string | null): boolean => {
        const localePattern = /^[a-z]{2}-[a-z]{2,}$/i

        return localePattern.test(locale ?? "")
    }

    public isIllegalCentraLocale = (i18n: I18n, centraLocale: string | null): boolean => {
        return centraLocale === null || !i18n.locales.includes(centraLocale)
    }

    public isStringEuroCurrencyCentraLocale = (string: string): boolean => {
        const lowerCasedString = string.toLowerCase()

        return (
            lowerCasedString === "en" ||
            lowerCasedString === "fr" ||
            lowerCasedString === "de"
        )
    }

    public hasShippingCountryEuroCurrency = (
        shippingCountryCode: CentraLocaleShippingCountryCode
    ): boolean => {
        return shippingCountryCodeToPriceList.get(shippingCountryCode) ? true : false
    }

    public extractCentraLocaleFromPath = (pathname: string): string | null => {
        const parts = pathname.split("/")
        const isLocaleEuroCurrencyLocale = this.isStringEuroCurrencyCentraLocale(parts[1])

        if (isLocaleEuroCurrencyLocale) {
            return parts[1]
        }

        const maybeCentraMarketLocale = this.isValidLocale(parts[1]) ? parts[1] : null

        return Object.is(maybeCentraMarketLocale, null) ? null : maybeCentraMarketLocale
    }

    public extractValidAndLegalCentraLocaleFromPath = (
        pathname: string
    ): string | null => {
        const parts = pathname.split("/")

        const isLocaleEuroCurrencyLocale = this.isStringEuroCurrencyCentraLocale(parts[1])

        if (isLocaleEuroCurrencyLocale) {
            return parts[1]
        }

        const maybeCentraMarketLocale = this.isValidLocale(parts[1]) ? parts[1] : null
        const maybeLegalCentraMarketLocale = this.isIllegalCentraLocale(
            i18n,
            maybeCentraMarketLocale
        )
            ? null
            : maybeCentraMarketLocale

        return Object.is(maybeLegalCentraMarketLocale, null)
            ? null
            : maybeLegalCentraMarketLocale
    }

    public constructCentraLocaleFromSelectionMetadataWithValidShippingCountry =
        (logger: Logger) =>
        (metadata: TokenAndSelectionMetadata["metadata"]): string => {
            try {
                if (
                    !isShippingCountryCode(metadata.country?.code, SHIPPING_COUNTRY_CODES)
                ) {
                    throw new Error(
                        `Invalid shipping country code: ${metadata.country?.code}. Make sure non-shipping countries are handled.`
                    )
                }

                const shippingCountry =
                    this.getCentraLocaleShippingCountryFromShippingCountryCode(
                        metadata.country?.code,
                        CENTRA_LOCALE_SHIPPING_COUNTRY_CODES
                    )
                const isEuroCurrencyShippingCountry = shippingCountry.length === 0

                const centraLocale = isEuroCurrencyShippingCountry
                    ? `${metadata?.language.toLowerCase() ?? ""}`
                    : `${metadata?.language.toLowerCase() ?? ""}-${shippingCountry}`

                if (!validateCentraLocale(centraLocale)) {
                    throw new Error(`Invalid Centra locale: ${centraLocale}`)
                }

                return centraLocale
            } catch (error) {
                logger.error(
                    "Error determining locale from token:",
                    error instanceof Error ? error.message : JSON.stringify(error)
                )

                throw new Error(
                    `Locale determination failed: ${error instanceof Error ? error.message : JSON.stringify(error)}`
                )
            }
        }

    public constructCentraLocaleFromUserLocaleWithValidShippingCountry =
        (logger: Logger) =>
        (userLocale: UserLocale): string => {
            try {
                if (
                    !isShippingCountryCode(userLocale.countryCode, SHIPPING_COUNTRY_CODES)
                ) {
                    throw new Error(
                        `Invalid shipping country code: ${userLocale.countryCode}. Make sure non-shipping countries are handled.`
                    )
                }

                const shippingCountry =
                    this.getCentraLocaleShippingCountryFromShippingCountryCode(
                        userLocale.countryCode,
                        CENTRA_LOCALE_SHIPPING_COUNTRY_CODES
                    )
                const isEuroCurrencyShippingCountry = shippingCountry.length === 0

                const path = userLocale.path.replace(/^\/+/, "").toLowerCase()
                const [langFromPath, countryFromPath] = path.split("-")

                const centraLocale = isEuroCurrencyShippingCountry
                    ? path
                    : countryFromPath === shippingCountry
                      ? path
                      : `${langFromPath}-${shippingCountry}`

                if (!validateCentraLocale(centraLocale)) {
                    throw new Error(`Invalid Centra locale: ${centraLocale}`)
                }

                return centraLocale
            } catch (error) {
                logger.error(
                    "Error determining locale from UserLocale cookie:",
                    error instanceof Error ? error.message : JSON.stringify(error)
                )

                throw new Error(
                    `Locale determination failed: ${
                        error instanceof Error ? error.message : JSON.stringify(error)
                    }`
                )
            }
        }

    /**
     * This method will make sure that countries with EUR currency will return an empty string.
     */
    public getCentraLocaleShippingCountryFromShippingCountryCode = (
        shippingCountryCode: ShippingCountryCode,
        centraLocaleShippingCountryCodes: typeof CENTRA_LOCALE_SHIPPING_COUNTRY_CODES
    ): CentraLocaleShippingCountryCode => {
        const centraLocaleCountry =
            centraLocaleShippingCountryCodes[
                shippingCountryCode.toLowerCase() as ShippingCountryCode
            ]

        return centraLocaleCountry ? centraLocaleCountry : ""
    }
}

export default CentraPath
